import React, { useMemo } from 'react';
import styled from 'styled-components';
import { FilledButton, FlexRow } from '@make-software/tg-mini-app-ui';

import { useVoteForm, SecondaryText, useWalletConnection, VoteProjectModal } from '@/shared';
import { shouldBeRedirectedToCWM, motesToCSPR } from '@/utils';

import { FeeText } from '../styled';
import { AmountBox } from './amount-box';
import { ButtonTitle } from './button-title';
import { ErrorText } from '../../home/components/styled';

export const Space = styled.div`
  margin-top: 12px;
`;

interface VoteFormProps {
  projectAddress?: string;
  refetchProjectDetails: () => void;
}

export const VoteForm: React.FC<VoteFormProps> = ({ projectAddress, refetchProjectDetails }) => {
  const {
    amount,
    sendMax,
    voteTxError,
    disabled,
    fetchPreviousVoteTxData,
    handleSelectPresetAmount,
    handleSendMax,
    handleSend,
    isInsufficientCSPRBalance,
    voteTxFee,
    expirationCountdown,
    voteAmounts,
    isTxModalOpen,
    handleTxModalClose,
    txStatus,
    submitting,
    isInsufficientFANSBalance,
    airdropInfo,
    remainingVotingCapacity,
    maxSelectableAmount,
    votingCapacityResetCountdown,
    votingCapacity,
    isVotingCapacityKnown
  } = useVoteForm({
    projectAddress,
    onProcessed: refetchProjectDetails
  });

  const handleWalletConnected = () => {
    fetchPreviousVoteTxData();
  };

  const { isWalletConnected } = useWalletConnection({ onWalletConnected: handleWalletConnected });

  let errorMessage = null;
  if (shouldBeRedirectedToCWM()) {
    errorMessage = (
      <ErrorText weight={3}>
        You'll be redirected to Casper Wallet to sign the transaction.
      </ErrorText>
    );
  } else if (!isWalletConnected) {
    errorMessage = (
      <ErrorText weight={3}>Please check your wallet connection before voting.</ErrorText>
    );
  } else if (window?.csprclick?.getActiveAccount() !== null && isInsufficientCSPRBalance) {
    errorMessage = (
      <ErrorText weight={3}>
        Your current CSPR balance is not enough to send this transaction.
      </ErrorText>
    );
  } else if (isInsufficientFANSBalance) {
    errorMessage = (
      <ErrorText weight={3}>
        Insufficient FAN balance, if you made a failed transaction please wait for tokens refund or
        earn some additional token to vote again.
      </ErrorText>
    );
  } else if (voteTxError) {
    errorMessage = <ErrorText weight={3}>{voteTxError}</ErrorText>;
  } else if (expirationCountdown?.active && !submitting) {
    errorMessage = (
      <ErrorText weight={3}>
        Your transaction expires in {expirationCountdown?.minutes}:{expirationCountdown?.seconds}.
        Wait until expiration to change your vote.
      </ErrorText>
    );
  }

  const sendButtonDisabled = useMemo(
    () =>
      amount <= 0 ||
      maxSelectableAmount <= 0 ||
      (!shouldBeRedirectedToCWM() && (disabled || !isWalletConnected)),
    [amount, disabled, isWalletConnected, maxSelectableAmount]
  );

  // on Season 5, daily voting capacity is capped at 1000 FAN
  const isDailyVotingCapped = true;

  const usingAllDailyCapacityWarning =
    isDailyVotingCapped &&
    isVotingCapacityKnown &&
    maxSelectableAmount > 0 &&
    amount === maxSelectableAmount ? (
      <ErrorText weight={3}>
        A maximum of 1000 FAN tokens can be used to vote every 24hrs.
      </ErrorText>
    ) : null;

  const noCapacityWarning =
    isDailyVotingCapped &&
    isVotingCapacityKnown &&
    votingCapacity != null &&
    votingCapacity === 0 ? (
      <ErrorText weight={3}>
        A maximum of 1000 FAN tokens can be used to vote every day during Round 2.
        {votingCapacityResetCountdown?.active
          ? ` Resets in ${
              votingCapacityResetCountdown.days > 0 ? `${votingCapacityResetCountdown.days}d ` : ''
            }${votingCapacityResetCountdown.hours}:${votingCapacityResetCountdown.minutes}:${votingCapacityResetCountdown.seconds}.`
          : ''}
      </ErrorText>
    ) : null;

  return (
    <>
      <FlexRow align="center" justify={'space-between'} gap={5} style={{ marginTop: '12px' }}>
        {voteAmounts?.map((amt, idx) => (
          <AmountBox
            key={idx}
            amount={amt}
            onSelectAmount={() => handleSelectPresetAmount(amt)}
            selected={amount === amt}
            currentRound={airdropInfo?.airdrop?.round}
          />
        ))}

        <AmountBox
          amount={`MAX (${remainingVotingCapacity})`}
          onSelectAmount={handleSendMax}
          selected={sendMax}
        />
      </FlexRow>

      <Space />
      <Space />

      <FlexRow align="center" justify="space-between">
        <SecondaryText weight={3} size="md" color="#FFFFFF" fontWeight={700} fontFamily="Inter">
          Transaction fee:
        </SecondaryText>
        <FeeText weight={3} size="md" color="#FFFFFF">
          {motesToCSPR(voteTxFee)} CSPR
        </FeeText>
      </FlexRow>

      <Space />

      <FilledButton
        title={
          <ButtonTitle
            amount={amount}
            isWalletConnected={shouldBeRedirectedToCWM() || isWalletConnected}
            disabled={sendButtonDisabled}
          />
        }
        width="100%"
        height={36}
        margin="0"
        disabled={sendButtonDisabled}
        onClick={handleSend}
        disabledStyles={{ backgroundColor: '#30313b' }}
      />

      <Space />

      {usingAllDailyCapacityWarning}

      {noCapacityWarning}

      {errorMessage}

      <VoteProjectModal isOpen={isTxModalOpen} onClose={handleTxModalClose} status={txStatus} />
    </>
  );
};
